import { AlertCircleIcon, ArrowDownIcon, ArrowUpIcon, BugIcon, CheckCircle2Icon, CircleDotIcon, CircleIcon, ClockIcon, MinusIcon, SparklesIcon, TriangleAlertIcon } from "lucide-react";

import type { Environment, Priority, Status, TicketType } from "./types";

export const PRIORITY_CONFIG: Record<Priority, { icon: React.ElementType; className: string; description: string; bg: string }> = {
  Critical: {
    icon: TriangleAlertIcon,
    className: "text-destructive",
    description: "Immediate action required",
    bg: "bg-destructive/10 border-destructive/30",
  },
  High: {
    icon: ArrowUpIcon,
    className: "text-amber-600 dark:text-amber-400",
    description: "Address in current sprint",
    bg: "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800/40",
  },
  Medium: {
    icon: MinusIcon,
    className: "text-muted-foreground",
    description: "Normal development priority",
    bg: "bg-muted border-border",
  },
  Low: {
    icon: ArrowDownIcon,
    className: "text-muted-foreground/60",
    description: "Tackle when capacity allows",
    bg: "bg-muted/50 border-border",
  },
};

export const STATUS_CONFIG: Record<Status, { icon: React.ElementType; className: string }> = {
  Open: { icon: CircleIcon, className: "text-foreground" },
  "In Progress": { icon: CircleDotIcon, className: "text-amber-600 dark:text-amber-400" },
  "In Review": { icon: SparklesIcon, className: "text-primary" },
  Resolved: { icon: CheckCircle2Icon, className: "text-primary" },
  Closed: { icon: CheckCircle2Icon, className: "text-muted-foreground" },
};

export const TYPE_CONFIG: Record<TicketType, { icon: React.ElementType; className: string; description: string }> = {
  Bug: {
    icon: BugIcon,
    className: "text-destructive",
    description: "Something isn't working",
  },
  Feature: {
    icon: SparklesIcon,
    className: "text-primary",
    description: "New capability or enhancement",
  },
  Task: {
    icon: ClockIcon,
    className: "text-muted-foreground",
    description: "A unit of work to complete",
  },
  Improvement: {
    icon: ArrowUpIcon,
    className: "text-amber-600 dark:text-amber-400",
    description: "Refine existing behaviour",
  },
};

export const STATUS_DOT: Record<Status, string> = {
  Open: "bg-muted-foreground",
  "In Progress": "bg-amber-500",
  "In Review": "bg-primary",
  Resolved: "bg-primary/50",
  Closed: "bg-border",
};

export const STATUS_COLOURS: Record<Status, string> = {
  Open: "text-foreground",
  "In Progress": "text-amber-600 dark:text-amber-400",
  "In Review": "text-primary",
  Resolved: "text-primary",
  Closed: "text-muted-foreground",
};

export const ALL_STATUSES: Status[] = ["Open", "In Progress", "In Review", "Resolved", "Closed"];

export const STATUS_TO_API: Record<Status, string> = {
  Open: "Open",
  "In Progress": "InProgress",
  "In Review": "InReview",
  Resolved: "Resolved",
  Closed: "Closed",
};

export const STATUS_FROM_API: Record<string, Status> = {
  Open: "Open",
  InProgress: "In Progress",
  InReview: "In Review",
  Resolved: "Resolved",
  Closed: "Closed",
};

export const ENVIRONMENTS: Environment[] = ["Production", "Staging", "Development", "Local"];

export const ENV_COLOURS: Record<Environment, string> = {
  Production: "text-destructive",
  Staging: "text-amber-600 dark:text-amber-400",
  Development: "text-primary",
  Local: "text-muted-foreground",
};

// Human-readable labels for the `field` values returned by
// GET /project/:id/tickets/:tid/history (see FieldNames.TicketHistoryField* on the backend).
export const HISTORY_FIELD_LABELS: Record<string, string> = {
  title: "Title",
  status: "Status",
  priority: "Priority",
  description: "Description",
  assignee: "Assignee",
  effort: "Story points",
  tags: "Tags",
  reproducing_steps: "Reproducing steps",
  expected_behaviour: "Expected behaviour",
  actual_behaviour: "Actual behaviour",
  affected_version: "Affected version",
  environment: "Environment",
};
